import React from "react";
import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SelectArrow from "../../../styles/icons/SelectArrow";
import {
  DEFAULT_LEAD_AGE_RESTRICTION,
  LEAD_AGE_DAY_OPTIONS,
  normalizeLeadAgeRestriction,
} from "../../../utils/workflow/leadAgeRestriction";

const COLOR_HEADER = "#1E1E1E";
const COLOR_ACCENT = "#0DA3E2";

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.5,
      maxHeight: 320,
      overflowY: "auto",
      bgcolor: COLOR_HEADER,
      backgroundImage: "none",
      color: "#FFFFFF",
      border: `1px solid ${COLOR_ACCENT}`,
      borderRadius: "3px",
      "& .MuiMenuItem-root": {
        minHeight: 42,
        color: "#FFFFFF",
        fontSize: "14px",
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: "rgba(13,163,226,0.22)",
        },
        "&:hover": {
          backgroundColor: "rgba(13,163,226,0.12)",
        },
      },
    },
  },
};

export default function LeadAgeRestrictionPanel({ value, onChange }) {
  const restriction = normalizeLeadAgeRestriction(value);
  const isRestricted = restriction.enabled;

  const handleModeChange = (enabled) => {
    onChange({
      enabled,
      minDays: restriction.minDays || DEFAULT_LEAD_AGE_RESTRICTION.minDays,
    });
  };

  const handleDaysChange = (event) => {
    onChange({
      enabled: true,
      minDays: Number(event.target.value),
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(173, 180, 189, 0.35)",
        borderRadius: "7px",
        overflow: "hidden",
        bgcolor: "#1E1E1E",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          bgcolor: "rgba(255,255,255,0.06)",
          borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Assistant, sans-serif",
            fontWeight: 700,
            fontSize: "16px",
            color: "#FFFFFF",
          }}
        >
          מגבלות שליחה
        </Typography>
        <Tooltip
          title="מסנן לידים לפי תאריך השארת הפרטים (Date). לידים שהשאירו פרטים בימים האחרונים לא ייכללו בשאילתה."
          placement="top"
        >
          <HelpOutlineIcon
            sx={{ fontSize: 18, color: "rgba(255,255,255,0.65)", cursor: "help" }}
          />
        </Tooltip>
      </Box>

      <Stack spacing={1} sx={{ px: 2, py: 1.5 }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Radio
              size="small"
              checked={!isRestricted}
              onChange={() => handleModeChange(false)}
              sx={{
                color: COLOR_ACCENT,
                "&.Mui-checked": { color: COLOR_ACCENT },
              }}
            />
          }
          label={
            <Typography sx={{ color: "#FFFFFF", fontSize: "14px" }}>
              ללא מגבלות
            </Typography>
          }
        />

        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Radio
            size="small"
            checked={isRestricted}
            onChange={() => handleModeChange(true)}
            sx={{
              color: COLOR_ACCENT,
              "&.Mui-checked": { color: COLOR_ACCENT },
            }}
          />
          <Typography sx={{ color: "#FFFFFF", fontSize: "14px" }}>
            אל תכלול לידים שהשאירו פרטים ב-
          </Typography>
          <Select
            size="small"
            value={restriction.minDays}
            onChange={handleDaysChange}
            disabled={!isRestricted}
            IconComponent={SelectArrow}
            MenuProps={SELECT_MENU_PROPS}
            sx={{
              minWidth: 72,
              height: 36,
              color: "#FFFFFF",
              bgcolor: COLOR_HEADER,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
                borderRadius: "4px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: COLOR_ACCENT,
              },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.45)",
              },
            }}
          >
            {LEAD_AGE_DAY_OPTIONS.map((days) => (
              <MenuItem key={days} value={days}>
                {days}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ color: "#FFFFFF", fontSize: "14px" }}>
            ימים האחרונים
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
